import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Paladino L2-5 (manuale V1, rr. 4970-5048). 16 mosse.
 *
 * Pattern notabili:
 * - ***Aura*** (D7): mossa-config con 5 scopi in `MoveSchema.choices`
 *   (`choiceCount: 1`).
 * - ***Crociato*** (multiclasse): condizionato dall'Ordine scelto (Sacro/
 *   Sovrannaturale). Description-only (FI-17). L'archetipo Elfo aggiunge
 *   Druido alle classi pescabili.
 * - ***Richiama Cavalcatura*** (FI-8 stub): description-only, coerente con
 *   *Compagno Animale* del Ranger.
 * - **3 mosse richiedono la mossa di casting** (Aura, Imposizione delle
 *   Mani richiedono *Faro di Devozione* + condizione di lancio): pattern
 *   `prerequisites.anyOf` cross-class già supportato.
 * - **2 *Giuramenti***: regole runtime ("massimo 1 attivo per volta"),
 *   description-only.
 */

const leguleio: Move = {
  id: 'paladino.leguleio',
  name: 'Leguleio',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando *declami conoscenze* riguardo a leggi, regolamenti o codici',
  description:
    'Hai +1 al tiro. Inoltre, con 12+, il GM ti dice anche un cavillo poco noto che puoi sfruttare a tuo vantaggio.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const egidaInsanguinata: Move = {
  id: 'paladino.egida-insanguinata',
  name: 'Egida Insanguinata',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando subisci dei danni',
  description:
    'Puoi stringere i denti e incassare il colpo. Se lo fai, non prendi alcun danno, ma devi segnare una Debilitazione a tua scelta che non hai già. Non puoi usare questa mossa se hai già tutte le Debilitazioni segnate.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const muroDiLame: Move = {
  id: 'paladino.muro-di-lame',
  name: 'Muro di Lame',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: "Quando scegli l'opzione di infliggere danni della mossa *difendere*",
  description: 'Infliggi +1d4 danni.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const difensoreDevoto: Move = {
  id: 'paladino.difensore-devoto',
  name: 'Difensore Devoto',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando *difendi*',
  description: 'Hai sempre +1 presa, anche con un 6−.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const scudoSacrificabile: Move = {
  id: 'paladino.scudo-sacrificabile',
  name: 'Scudo Sacrificabile',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando stai *difendendo* qualcuno o qualcosa e imbracci uno scudo',
  description:
    "Puoi lasciare che lo scudo venga distrutto e annullare completamente i danni o l'effetto di un'azione ostile compiuta contro ciò che stai proteggendo, senza dover spendere una presa.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const attaccoASeguire: Move = {
  id: 'paladino.attacco-a-seguire',
  name: 'Attacco a Seguire',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando *tagli e spacchi*',
  description:
    'Scegli un alleato. Se il suo prossimo attacco contro il tuo bersaglio va a segno, infligge +1d4 danni.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const carica: Move = {
  id: 'paladino.carica',
  name: 'Carica!',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando guidi la carica contro il nemico',
  description: 'Coloro che ti seguono prendono +1 al prossimo tiro.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const laVoceDellAutorita: Move = {
  id: 'paladino.la-voce-dell-autorita',
  name: "La Voce dell'Autorità",
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando *dai ordini a un gregario*',
  description:
    'Prendi +1 al tiro.\n\n**FI-8 stub**: la mossa *dare ordini a un gregario* è gestita runtime al tavolo finché il sistema Seguaci a Ranghi 1-3 non sarà strutturato.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const giuramentoDiDevozione: Move = {
  id: 'paladino.giuramento-di-devozione',
  name: 'Giuramento di Devozione',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando giuri ad alta voce di proteggere una persona, un oggetto o un luogo',
  description:
    'Se gli sei vicino e viene compiuta un\'azione ostile contro di esso, puoi scegliere una singola opzione di *difendere* per proteggerlo, senza spendere prese, anche se non lo stai *difendendo* attivamente. Mentre l\'effetto è attivo non puoi *difendere* niente di diverso dal bersaglio prescelto. Il giuramento rimane valido finché ciò che hai giurato di proteggere non è ragionevolmente al sicuro. Se non ci riesci puoi ammettere il tuo fallimento, ma gli effetti di questa mossa permangono comunque fino a quando non troverai il modo per redimerti (a discrezione del GM). Puoi avere un solo *Giuramento* attivo per volta.\n\n**NB architetturale**: lo stato "giuramento attivo" è gestito a runtime al tavolo. Description-only.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const giuramentoDiVendetta: Move = {
  id: 'paladino.giuramento-di-vendetta',
  name: 'Giuramento di Vendetta',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando giuri ad alta voce di sconfiggere un nemico',
  description:
    "Infliggi +2d4 contro di lui e -1d4 danni contro chiunque altro. L'effetto dura finché il nemico non è stato sconfitto. Se non ci riesci, o ti arrendi, puoi ammettere il tuo fallimento, ma gli effetti di questa mossa permangono fino a quando non troverai un modo per redimerti (a discrezione del GM). Puoi avere un solo *Giuramento* attivo per volta.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const aura: Move = {
  id: 'paladino.aura',
  name: 'Aura',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger:
    "Quando hai qualche minuto di tranquillità per entrare in risonanza con il potere dell'entità cui sei devoto",
  description:
    "Scegli uno scopo (*vendetta*, *devozione*, *pietà*, *terrore* o *purezza*) e tira+**CAR**. Con 10+, ottieni 2 prese. Con 7-9, ne ottieni 1. In base allo scopo prescelto, nel momento opportuno puoi spendere le tue prese per:\n\n- *vendetta*: infliggere 1d6 danni con etichetta *consacrato a [entità cui sei devoto]* a un nemico (solo 1d6 per presa, senza aggiungere altri bonus);\n- *devozione*: per ogni presa, scegliere un'opzione della mossa *difendere* per proteggere te stesso o un alleato (puoi farlo anche se non lo stai *difendendo* attivamente);\n- *pietà*: lenire il dolore e curare qualcun altro di 1d6 PF per presa;\n- *terrore*: spaventare un avversario (una presa). Le creature più deboli sono terrorizzate, quelle più forti sono impressionate dal tuo potere. Il GM deciderà come reagiranno;\n- *purezza*: per ogni presa, annullare un effetto di controllo mentale o paura che stia interessando qualcun altro. Gli effetti più potenti (a discrezione del GM) sono solo sospesi per un po'.\n\nPuoi spendere anche più di una presa per volta, ma comunque l'effetto riguarderà un solo bersaglio entro distanza *lunga*. Con 6-, non ottieni prese, e non potrai usare di nuovo questa mossa finché non avrai modo di purificare la tua anima (il GM ti dirà come).",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Ottieni 2 prese di Aura.' },
    on7to9: { text: 'Ottieni 1 presa di Aura.' },
    on6minus: {
      text: 'Niente prese; non puoi usare di nuovo Aura finché non purifichi la tua anima.',
    },
  },
  tags: [],
  // Manuale r. 5009: "Richiede: *Faro di Devozione*, *Divinità*, *Divinità
  // della Natura* o *Contratto*". Coerente con *Io Sono la Legge* / *Missione*.
  prerequisites: [
    {
      anyOf: [
        'paladino.faro-di-devozione',
        'chierico.divinita',
        'druido.divinita-della-natura',
        'warlock.contratto',
      ],
    },
  ],
  replaces: [],
  choices: [
    { id: 'vendetta', label: 'Vendetta (danni con etichetta consacrato)' },
    { id: 'devozione', label: 'Devozione (opzione di difendere)' },
    { id: 'pieta', label: 'Pietà (cura 1d6 PF per presa)' },
    { id: 'terrore', label: 'Terrore (spaventa un avversario)' },
    { id: 'purezza', label: 'Purezza (annulla controllo mentale/paura)' },
  ],
  choiceCount: 1,
};

const auraVersatile: Move = {
  id: 'paladino.aura-versatile',
  name: 'Aura Versatile',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando usi la mossa *Aura*',
  description:
    "Scegli due scopi anziché uno. Quando spendi una presa di *Aura* puoi scegliere di attivare l'effetto di uno dei due scopi. Se usi più di una presa, per ognuna che spendi potrai ottenere l'effetto di uno qualsiasi dei due scopi scelti, ma il bersaglio resterà comunque lo stesso.",
  tags: [],
  prerequisites: ['paladino.aura'],
  replaces: [],
  // FI-31 (D5): concede un secondo scopo residuo dalle `choices` di
  // `paladino.aura` (mossa-config con 5 scopi, 1 già scelto al L1).
  grantsMoveChoice: { kind: 'from-choice-move', moveId: 'paladino.aura' },
};

const ospitaliere: Move = {
  id: 'paladino.ospitaliere',
  name: 'Ospitaliere',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando guarisci qualcun altro, non importa come',
  description:
    'Lo curi di +1d4 PF. Se usi *Imposizione delle Mani*, il bonus si applica anche se non scegli di curare i PF del bersaglio.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const crociato: Move = {
  id: 'paladino.crociato',
  name: 'Crociato',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli una mossa non multiclasse tra quelle del Guerriero, del Chierico (solo se hai la mossa *Ordine Sacro*), oppure del Warlock (solo se hai la mossa *Ordine Sovrannaturale*).\n\n**NB**: con archetipo Elfo, puoi sempre scegliere anche tra le mosse del Druido.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-37 + FI-43 (2026-05-15, manuale V1 rr. 5035-5039 + r. 4847): pool base
  // Guerriero, + Chierico se *Ordine Sacro*, + Warlock se *Ordine Sovrannaturale*,
  // + Druido se archetipo Elfo.
  multiclass: {
    picks: { kind: 'fixed-classes', classes: ['guerriero'] },
    extendsByArchetype: [{ archetypeId: 'elfo', extraClasses: ['druido'] }],
    extendsByMove: [
      { moveId: 'paladino.ordine-sacro', extraClasses: ['chierico'] },
      { moveId: 'paladino.ordine-sovrannaturale', extraClasses: ['warlock'] },
    ],
  },
};

const richiamaCavalcatura: Move = {
  id: 'paladino.richiama-cavalcatura',
  name: 'Richiama Cavalcatura',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger: 'Quando la chiami con un fischio',
  description:
    'Hai una cavalcatura fedele. Quando la chiami con un fischio, accorre al tuo fianco. Se la tua cavalcatura dovesse morire o non essere più disponibile per qualsiasi altra ragione, potrai sempre trovare un sostituto, ma dovrà essere la creatura giusta e dovrai creare con essa un legame speciale.\n\n**FI-8 stub**: la cavalcatura è un seguace assimilabile al Compagno Animale del Ranger. Refactor unificato Ranghi 1-3 nella PR FI-8 post-12 classi. FI-18 Fase 1 struttura solo il **nome** + **descrizione** narrativi; lo strutturale del seguace (PF, Lealtà, Capacità utili) arriva con FI-8.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-18: setup-levelup narrative fields. Intersezione FI-8 (gregario).
  narrativeFields: [
    {
      id: 'nome-cavalcatura',
      label: 'Nome della cavalcatura',
      placeholder: 'es. Tempesta, Aurora, Falcone…',
      required: true,
      multiline: false,
    },
    {
      id: 'descrizione-cavalcatura',
      label: 'Descrizione della cavalcatura',
      placeholder: 'Specie, aspetto, segni particolari, indole…',
      required: false,
      multiline: true,
    },
  ],
};

const imposizioneDelleMani: Move = {
  id: 'paladino.imposizione-delle-mani',
  name: 'Imposizione delle Mani',
  type: 'advanced_2_5',
  classId: 'paladino',
  trigger:
    "Quando tocchi qualcun altro, pelle contro pelle, e invochi l'entità cui sei devoto affinché ti conceda di curarlo",
  description:
    "Tira+**CAR**. Con un 10+, lo guarisci di 1d8 PF, oppure arresti un'emorragia, rinsaldi le sue ossa, curi un veleno o rimuovi una malattia. Con un 7-9, lo stesso, ma anche tu subisci 1d8 danni (*ignora armatura*), oppure l'effetto che hai curato viene trasferito a te.",
  roll: {
    stat: 'carisma',
    on10plus: {
      text: 'Lo guarisci di 1d8 PF, oppure arresti emorragia/rinsaldi ossa/curi veleno/rimuovi malattia.',
    },
    on7to9: {
      text: "Lo stesso del 10+, ma anche tu subisci 1d8 danni (ignora armatura) o l'effetto curato si trasferisce a te.",
    },
  },
  tags: [],
  prerequisites: [
    {
      anyOf: [
        'paladino.faro-di-devozione',
        'chierico.divinita',
        'druido.divinita-della-natura',
        'warlock.contratto',
      ],
    },
  ],
  replaces: [],
};

export const paladinoAdvanced2to5Moves: readonly Move[] = [
  leguleio,
  egidaInsanguinata,
  muroDiLame,
  difensoreDevoto,
  scudoSacrificabile,
  attaccoASeguire,
  carica,
  laVoceDellAutorita,
  giuramentoDiDevozione,
  giuramentoDiVendetta,
  aura,
  auraVersatile,
  ospitaliere,
  crociato,
  richiamaCavalcatura,
  imposizioneDelleMani,
] as const;
